import { isUserCancelledFileAction } from './report-export-file-action.utils';
import { normalizeExcelFilename } from './report-export-file-name.utils';
import type { ReportExportFileService } from './report-export-file.service';

function toArrayBuffer(buffer: ArrayBuffer | BlobPart): Promise<ArrayBuffer> {
  if (buffer instanceof ArrayBuffer) {
    return Promise.resolve(buffer);
  }

  if (buffer instanceof Blob) {
    return buffer.arrayBuffer();
  }

  return new Blob([buffer]).arrayBuffer();
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const chunkSize = 0x8000;

  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }

  const base64Encoder = globalThis.btoa;
  if (typeof base64Encoder !== 'function') {
    throw new Error('Base64 encoder is not available');
  }

  return base64Encoder(binary);
}

export class NativeReportExportFileService implements ReportExportFileService {
  async saveExcelFile(buffer: ArrayBuffer | BlobPart, filename: string): Promise<void> {
    const [{ Filesystem, Directory }, { Share }] = await Promise.all([
      import('@capacitor/filesystem'),
      import('@capacitor/share'),
    ]);

    const finalFilename = normalizeExcelFilename(filename);
    const path = `reports/${finalFilename}`;
    const data = arrayBufferToBase64(await toArrayBuffer(buffer));

    const writeResult = await Filesystem.writeFile({
      path,
      directory: Directory.Documents,
      data,
      recursive: true,
    });

    const uri = writeResult.uri ?? (await Filesystem.getUri({ path, directory: Directory.Documents })).uri;
    try {
      await Share.share({
        title: finalFilename,
        dialogTitle: finalFilename,
        url: uri,
      });
    } catch (error) {
      if (isUserCancelledFileAction(error)) {
        return;
      }
      throw error;
    }
  }
}
